import type { PaginatedResponseType } from '~/types/api'
import api from './api'

/** El descuento se pacta en dinero ('amount') o como porcentaje ('percent'). */
export type DiscountTypeType = 'amount' | 'percent'

export type OrderStatusType = 'pending' | 'paid' | 'cancelled' | 'refunded' | 'expired'

export type PaymentStatusType = 'pending' | 'approved' | 'rejected' | 'refunded' | 'cancelled'

export interface OrderItemType {
  id: number
  orderId: number
  productId?: number | null
  variantId?: number | null
  /** Congelados en el momento de la compra: si el producto cambia, esto no. */
  sku?: string | null
  name: string
  description?: string | null
  /** Las columnas `numeric` llegan como texto desde la API: normalizar con toNumber. */
  quantity: number | string
  unitPrice: number | string
  /** Coste del catálogo al vender; es lo que permite calcular el margen. */
  unitCost?: number | string | null
  tariff?: string | null
  discount: number | string
  discountType: DiscountTypeType
  subtotal: number | string
  order: number
}

export interface PaymentType {
  id: number
  orderId: number
  gatewayId?: number | null
  provider: string
  mode: 'sandbox' | 'live'
  externalId?: string | null
  externalReference?: string | null
  status: PaymentStatusType
  amount: number | string
  currency: string
  method?: string | null
  installments?: number | null
  checkoutUrl?: string | null
  failureReason?: string | null
  approvedAt?: string | null
  createdAt: string
}

export interface OrderType {
  id: number
  companyId: string
  number: string
  customerId?: number | null
  userId?: number | null
  status: OrderStatusType
  currency: string
  subtotal: number | string
  taxRate: number | string
  taxAmount: number | string
  discount: number | string
  discountType: DiscountTypeType
  discountAmount: number | string
  total: number | string
  /** Lo que tecleó el comprador; puede no coincidir con el cliente del sistema. */
  buyer?: { name?: string; email?: string; phone?: string; document?: string } | null
  shipping?: Record<string, unknown> | null
  notes?: string | null
  paidAt?: string | null
  /**
   * Cuándo se descontó el inventario. `paid` con esto en null significa que se
   * cobró y el stock NO se pudo aplicar: hay que mirarlo.
   */
  stockAppliedAt?: string | null
  items?: OrderItemType[]
  payments?: PaymentType[]
  customer?: { id: number; name: string; email?: string | null; document?: string | null } | null
  createdAt: string
  updatedAt: string
}

export interface OrderListParamsType {
  page?: number
  limit?: number
  q?: string
  status?: OrderStatusType | ''
  customerId?: number
  from?: string
  to?: string
}

export const orderService = {
  async list(params?: OrderListParamsType) {
    const { data } = await api.get('/orders', { params })
    return data.data as PaginatedResponseType<OrderType>
  },

  async get(id: number) {
    const { data } = await api.get(`/orders/${id}`)
    return data.data as OrderType
  },

  /** Genera el enlace de pago para mandárselo al cliente. */
  async pay(id: number) {
    const { data } = await api.post(`/orders/${id}/pay`)
    return data.data as { payment: PaymentType; checkoutUrl: string }
  },

  /** Le pregunta a la pasarela cómo quedaron los pagos. Útil si no llegó el aviso. */
  async sync(id: number) {
    const { data } = await api.post(`/orders/${id}/sync`)
    return data.data as PaymentType[]
  },

  async cancel(id: number) {
    const { data } = await api.post(`/orders/${id}/cancel`)
    return data.data as OrderType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/orders/${id}`)
    return data.data as boolean
  },

  /** Elimina varios pedidos de una vez. Los que tienen pagos se omiten. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/orders/bulk-delete', { ids })
    return data.data as { deleted: number; skipped: number }
  }
}

export default orderService
